import { useMemo } from "react";
import { Tooltip } from "@/components/atoms/Tooltip";
import { WEEKDAY_CODE_BY_INDEX } from "@/types/availability";
import { addDays, formatMinutes, type SlotProjection } from "./slotProjection";
import { RAMP_LOAD, rampStep } from "./sceneTheme";

interface Props {
  projection: SlotProjection;
  /** Anchors the horizon; the grid always starts from "today". */
  now: Date;
}

/**
 * The whole generation horizon as a day × hour grid.
 *
 * The 3D plate shows one week; the settings generate thirty days. This is the
 * only view that fits the entire horizon at once, and it turns the notice
 * cutoff and the unselected weekdays into visible structure rather than
 * per-tile greying.
 *
 * Magnitude, so a single hue light→dark with a scale legend.
 */
export function SlotHeatmap({ projection, now }: Props) {
  const { hours, grid, max } = useMemo(() => {
    const start = projection.windowStartMinutes ?? 0;
    const end = projection.windowEndMinutes ?? 0;
    const rows: number[] = [];
    for (let h = Math.floor(start / 60); h < Math.ceil(end / 60); h += 1) rows.push(h);

    const activeCodes = new Set(projection.days.filter((d) => d.active).map((d) => d.weekdayCode));
    const noticeCutoff = new Date(now.getTime() + (projection.horizonDays > 0 ? 0 : 0));
    let peak = 1;

    const cells: number[][] = [];
    for (let dayOffset = 0; dayOffset < projection.horizonDays; dayOffset += 1) {
      const date = addDays(noticeCutoff, dayOffset);
      const runs =
        projection.active && activeCodes.has(WEEKDAY_CODE_BY_INDEX[date.getDay()]);
      cells.push(
        rows.map((h) => {
          if (!runs) return 0;
          const count = projection.dailyStarts.filter((t) => Math.floor(t / 60) === h).length;
          peak = Math.max(peak, count);
          return count;
        }),
      );
    }
    return { hours: rows, grid: cells, max: peak };
  }, [projection, now]);

  if (hours.length === 0 || grid.length === 0) {
    return <EmptyHeat />;
  }

  return (
    <div className="absolute inset-0 flex flex-col gap-2 px-3 py-3">
      <div className="flex items-center gap-2 text-[11.5px] text-bridge-subtle">
        <span className="font-mono uppercase tracking-[.11em]">Slots per hour</span>
        <span className="flex gap-[2px]">
          <span className="h-2.5 w-4 rounded-[2px] bg-bridge-border" />
          {RAMP_LOAD.map((c) => (
            <span key={c} className="h-2.5 w-4 rounded-[2px]" style={{ background: c }} />
          ))}
        </span>
        <span className="font-mono">0 → {max}</span>
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        <div
          className="grid gap-[2px]"
          style={{ gridTemplateColumns: `28px repeat(${grid.length}, minmax(6px, 1fr))` }}
        >
          {hours.map((hour, r) => (
            <Row
              key={hour}
              hour={hour}
              values={grid.map((col) => col[r])}
              max={max}
              startDate={now}
            />
          ))}
        </div>
      </div>

      <p className="text-[11.5px] text-bridge-subtle">
        {projection.horizonDays} days from today · rows are hours of the availability window
      </p>
    </div>
  );
}

function Row({
  hour,
  values,
  max,
  startDate,
}: {
  hour: number;
  values: number[];
  max: number;
  startDate: Date;
}) {
  return (
    <>
      <span className="pr-1 text-right font-mono text-[10.5px] leading-5 text-bridge-subtle">
        {formatMinutes(hour * 60).replace(":00", "")}
      </span>
      {values.map((count, i) => (
        <Tooltip
          key={i}
          side="top"
          content={`${addDays(startDate, i).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })} · ${count} slot${count === 1 ? "" : "s"}`}
        >
          <span
            className="block h-5 rounded-[2px]"
            style={{
              background: count === 0 ? "rgb(239 243 249)" : rampStep(RAMP_LOAD, count / max),
            }}
          />
        </Tooltip>
      ))}
    </>
  );
}

function EmptyHeat() {
  return (
    <div className="absolute inset-0 flex items-center justify-center">
      <p className="max-w-[220px] text-center text-xs text-bridge-subtle">
        Nothing to map yet — the settings generate no slots.
      </p>
    </div>
  );
}
